import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { I18nProvider, detectLangFromPath, langBasename } from "@/lib/i18n";
import { AuthProvider } from "@/lib/auth";
import Home from "@/pages/Home";
import Quote from "@/pages/Quote";
import ServicesIndex from "@/pages/ServicesIndex";
import ServicePage from "@/pages/ServicePage";
import ServiceCityPage from "@/pages/ServiceCityPage";
import TarifsIndex from "@/pages/TarifsIndex";
import TarifsService from "@/pages/TarifsService";
import GuidesIndex from "@/pages/GuidesIndex";
import GuideArticle from "@/pages/GuideArticle";
import OutilsIndex from "@/pages/OutilsIndex";
import CalculateurVolume from "@/pages/CalculateurVolume";
import CalculateurCartons from "@/pages/CalculateurCartons";
import InventaireDemenagement from "@/pages/InventaireDemenagement";
import RealisationsIndex from "@/pages/RealisationsIndex";
import EtudesIndex from "@/pages/EtudesIndex";
import BarometreVaud2026 from "@/pages/BarometreVaud2026";
import ProfessionnelsIndex from "@/pages/ProfessionnelsIndex";
import ProfessionnelsSegment from "@/pages/ProfessionnelsSegment";
import NotFound from "@/pages/NotFound";
import WhatsAppFab from "@/components/WhatsAppFab";
import ScrollToTop from "@/components/ScrollToTop";
import LiveTracker from "@/components/LiveTracker";

// Lazy-loaded routes: they ship in separate chunks, keeping the initial
// bundle lean. Landing users get Home + Quote + Services shipped eagerly;
// everything else is fetched on demand.
const Reservation = lazy(() => import("@/pages/Reservation"));
const About = lazy(() => import("@/pages/About"));
const Help = lazy(() => import("@/pages/Help"));
const Careers = lazy(() => import("@/pages/Careers"));
const JobDetail = lazy(() => import("@/pages/JobDetail"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const EspaceClient = lazy(() => import("@/pages/EspaceClient"));
const CantonPage = lazy(() => import("@/pages/CantonPage"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const ZonesIndex = lazy(() => import("@/pages/ZonesIndex"));
const Suivi = lazy(() => import("@/pages/Suivi"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminJobs = lazy(() => import("@/pages/admin/AdminJobs"));
const AdminJobForm = lazy(() => import("@/pages/admin/AdminJobForm"));
const AdminJobApplications = lazy(() => import("@/pages/admin/AdminJobApplications"));
const AdminCareersContent = lazy(() => import("@/pages/admin/AdminCareersContent"));
const AdminBilling = lazy(() => import("@/pages/admin/AdminBilling"));
const AdminCompany = lazy(() => import("@/pages/admin/AdminCompany"));
const AdminClients = lazy(() => import("@/pages/admin/AdminClients"));
const AdminClientDetail = lazy(() => import("@/pages/admin/AdminClientDetail"));
const AdminTrackers = lazy(() => import("@/pages/admin/AdminTrackers"));
const AdminProspection = lazy(() => import("@/pages/admin/AdminProspection"));
const AdminLive = lazy(() => import("@/pages/admin/AdminLive"));

// Minimal fallback shown while a lazy chunk is loading. Reprend le logo
// Mopper avec une animation « gonfle/dégonfle » (mpBreath, définie dans index.html).
const RouteFallback = () => (
  <div style={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#F7F7F8", zIndex: 0 }}>
    <img
      src="/branding/logo-blue.png"
      alt="Mopper"
      style={{ width: 160, height: "auto", animation: "mpBreath 1.6s ease-in-out infinite", transformOrigin: "center" }}
    />
  </div>
);

function App() {
  const basename = langBasename(detectLangFromPath(window.location.pathname));
  return (
    <HelmetProvider>
      <div className="App">
        <I18nProvider>
          <AuthProvider>
            <BrowserRouter basename={basename}>
              <ScrollToTop />
              <LiveTracker />
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/reservation" element={<Reservation />} />
                  <Route path="/devis" element={<Quote />} />
                  <Route path="/services" element={<ServicesIndex />} />
                  <Route path="/services/:slug" element={<ServicePage />} />
                  <Route path="/services/:slug/:ville" element={<ServiceCityPage />} />
                  <Route path="/tarifs" element={<TarifsIndex />} />
                  <Route path="/tarifs/:slug" element={<TarifsService />} />
                  <Route path="/guides" element={<GuidesIndex />} />
                  <Route path="/guides/:slug" element={<GuideArticle />} />
                  <Route path="/outils" element={<OutilsIndex />} />
                  <Route path="/outils/calculateur-volume-demenagement" element={<CalculateurVolume />} />
                  <Route path="/outils/calculateur-cartons" element={<CalculateurCartons />} />
                  <Route path="/outils/inventaire-demenagement" element={<InventaireDemenagement />} />
                  <Route path="/realisations" element={<RealisationsIndex />} />
                  <Route path="/etudes" element={<EtudesIndex />} />
                  <Route path="/etudes/barometre-prix-demenagement-vaud-2026" element={<BarometreVaud2026 />} />
                  <Route path="/professionnels" element={<ProfessionnelsIndex />} />
                  <Route path="/professionnels/:slug" element={<ProfessionnelsSegment />} />
                  <Route path="/zones" element={<ZonesIndex />} />
                  <Route path="/zones/:slug" element={<CantonPage />} />
                  <Route path="/villes/:slug" element={<CityPage />} />
                  <Route path="/a-propos" element={<About />} />
                  <Route path="/aide" element={<Help />} />
                  <Route path="/carriere" element={<Careers />} />
                  <Route path="/carrieres" element={<Careers />} />
                  <Route path="/carrieres/:slug" element={<JobDetail />} />
                  <Route path="/connexion" element={<Login />} />
                  <Route path="/inscription" element={<Register />} />
                  <Route path="/espace-client" element={<EspaceClient />} />
                  <Route path="/admin" element={<AdminLogin />} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/bookings" element={<AdminDashboard />} />
                  <Route path="/admin/jobs" element={<AdminJobs />} />
                  <Route path="/admin/jobs/new" element={<AdminJobForm />} />
                  <Route path="/admin/jobs/:id/edit" element={<AdminJobForm />} />
                  <Route path="/admin/jobs/:id/applications" element={<AdminJobApplications />} />
                  <Route path="/admin/careers/content" element={<AdminCareersContent />} />
                  <Route path="/admin/billing" element={<AdminBilling />} />
                  <Route path="/admin/clients" element={<AdminClients />} />
                  <Route path="/admin/clients/:clientId" element={<AdminClientDetail />} />
                  <Route path="/admin/company" element={<AdminCompany />} />
                  <Route path="/admin/trackers" element={<AdminTrackers />} />
                  <Route path="/admin/prospection" element={<AdminProspection />} />
                  <Route path="/admin/live" element={<AdminLive />} />
                  <Route path="/suivi/:token" element={<Suivi />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <WhatsAppFab />
            </BrowserRouter>
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </I18nProvider>
      </div>
    </HelmetProvider>
  );
}

export default App;
